/**
 * Serviço responsável por gerenciar o armazenamento de dados no IndexedDB,
 * garantindo a persistência offline para processos e outras entidades.
 * Suporta operações de criação, leitura e remoção, com foco na consistência dos dados
 * em cenários offline e sincronização automática.
 */

import { Injectable } from '@angular/core';
import { StorageMap, JSONSchema } from '@ngx-pwa/local-storage';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { Process } from '../../models/process.model';
import { IDBPDatabase, openDB } from 'idb';

@Injectable({
  providedIn: 'root',
})
export class IndexedDbService {
  private db: Promise<IDBPDatabase>;
  private processSchema: JSONSchema = {
    type: 'array',
    items: {
      type: 'object',
      properties: {
        id: { type: 'number' },
        idIntegracao: { type: 'number' },
        codigo: { type: 'string' },
        fase: { type: 'string' },
        descricao: { type: 'string' },
        numeroProcesso: { type: 'string' },
        idTipoProcesso: { type: 'number' },
        nomeTipoProcesso: { type: 'string' },
        idModalidade: { type: 'number' },
        idModalidadeIntegracao: { type: 'number' },
        idModalidadeGrupo: { type: 'number' },
        idModalidadeGrupoIntegracao: { type: 'number' },
        empresa: {
          type: 'object',
          properties: {
            id: { type: 'number' },
            idIntegracao: { type: 'number' },
            codigo: { type: 'string' },
            cpfCnpj: { type: 'string' },
            nome: { type: 'string' },
            email: { type: 'string' },
            inscricaoMunicipal: { type: 'string' },
            telefone: { type: 'string' },
            situacao: { type: 'string' },
            endereco: {
              type: 'object',
              properties: {
                id: { type: 'number' },
                cep: { type: 'string' },
                logradouro: { type: 'string' },
                numero: { type: 'string' },
                complemento: { type: 'string' },
                bairro: { type: 'string' },
                cidade: { type: 'string' },
                referencia: { type: 'string' },
                uf: { type: 'string' },
                principal: { type: 'boolean' },
                tipoEndereco: { type: 'string' }
              }
            }
          },
          required: ['id', 'codigo', 'nome', 'cpfCnpj', 'endereco']
        },
        imovel: {
          type: 'object',
          properties: {
            id: { type: 'number' },
            idIntegracao: { type: 'number' },
            codigo: { type: 'string' },
            inscricaoImobiliaria: { type: 'string' },
            unidade: { type: 'string' },
            descricao: { type: 'string' },
            situacao: { type: 'string' },
            tipoImovel: { type: 'string' },
            endereco: {
              type: 'object',
              properties: {
                id: { type: 'number' },
                cep: { type: 'string' },
                logradouro: { type: 'string' },
                numero: { type: 'string' },
                complemento: { type: 'string' },
                bairro: { type: 'string' },
                cidade: { type: 'string' },
                referencia: { type: 'string' },
                uf: { type: 'string' },
                principal: { type: 'boolean' },
                tipoEndereco: { type: 'string' }
              }
            }
          }
        },
        dataDeInicio: { type: 'string' },
        situacao: { type: 'string' },
        etapas: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              id: { type: 'number' },
              ordem: { type: 'string' },
              descricao: { type: 'string' },
              responsavel: { type: 'string' },
              situacao: { type: 'string' },
              inicio: { type: 'string' },
              prazo: { type: 'string' }
            }
          }
        },
        isSynced: { type: 'boolean' }
      },
      required: ['id', 'codigo', 'descricao', 'empresa', 'imovel', 'dataDeInicio', 'situacao']
    }
  };

  constructor(private storage: StorageMap) {
    this.db = openDB('ArborApp', 1, {
      upgrade(db) {
        db.createObjectStore('numberReservations');
      }
    });
  }


  saveProcess(key: string, process: Process): Observable<boolean> {
    return this.storage.get<Process[]>(key, this.processSchema).pipe(
      map((processes) => processes || []),
      map((processes) => [...processes, process]),
      mergeMap((updatedProcesses) => this.storage.set(key, updatedProcesses)),
      map(() => true),
      catchError((err) => {
        console.error(`Error saving to IndexedDB: ${err}`);
        return of(false);
      })
    );
  }

  getProcesses(key: string): Observable<Process[]> {
    return this.storage.get<Process[]>(key, this.processSchema).pipe(
      map((processes) => processes || []),
      catchError((err) => {
        console.error(`Error retrieving from IndexedDB: ${err}`);
        return of([]);
      })
    );
  }

  updateProcess(key: string, updatedProcess: Process): Observable<boolean> {
    return this.getProcesses(key).pipe(
      map((processes) => {
        const index = processes.findIndex((p) => p.id === updatedProcess.id);
        if (index !== -1) {
          processes[index] = updatedProcess;
          return processes;
        }
        return [...processes, updatedProcess];
      }),
      mergeMap((processes) => this.storage.set(key, processes)),
      map(() => true),
      catchError((err) => {
        console.error(`Error updating process in IndexedDB: ${err}`);
        return of(false);
      })
    );
  }

  removeProcess(key: string, id: number): Observable<boolean> {
    return this.getProcesses(key).pipe(
      map((processes) => processes.filter((p) => p.id !== id)),
      mergeMap((updatedProcesses) => this.storage.set(key, updatedProcesses)),
      map(() => true),
      catchError((err) => {
        console.error(`Error removing process from IndexedDB: ${err}`);
        return of(false);
      })
    );
  }

  save<T>(key: string, items: T[], schema: JSONSchema, data: T): Observable<boolean> {
    return this.storage.set(key, data).pipe(
      map(() => true),
      catchError((err) => {
        console.error(`Erro ao salvar no IndexedDB: ${err}`);
        return of(false);
      })
    );
  }

  get<T>(key: string, schema: JSONSchema, p0: {}): Observable<T | undefined> {
    return this.storage.get<T>(key, schema).pipe(
      map((value) => value as T | undefined),
      catchError((err) => {
        console.error(`Erro ao buscar no IndexedDB: ${err}`);
        return of(undefined);
      })
    );
  }

  remove(key: string): Observable<boolean> {
    return this.storage.delete(key).pipe(
      map(() => true),
      catchError((err) => {
        console.error(`Erro ao remover do IndexedDB: ${err}`);
        return of(false);
      })
    );
  }

  handleSyncError(process: Process): Observable<boolean> {
    return this.updateProcess('offlineProcesses', process).pipe(
      catchError(err => {
        console.error(`Erro ao sincronizar processo: ${err}`);
        return of(false);
      })
    );
  }
}